import { FunctionsError } from 'firebase/functions'
import { i18n } from '@/i18n'

/** Normalized shape for any error thrown by a Cloud Functions callable. */
export interface CallableError {
  code: string
  reason?: string
  message: string
}

function isFunctionsError(error: unknown): error is FunctionsError {
  return error instanceof FunctionsError
}

// Hazard: an already-parsed error is a plain object; without this guard a second pass collapses it to "unknown".
function isCallableError(error: unknown): error is CallableError {
  return (
    typeof error === 'object' &&
    error !== null &&
    typeof (error as Record<string, unknown>).code === 'string' &&
    typeof (error as Record<string, unknown>).message === 'string'
  )
}

function extractReason(details: unknown): string | undefined {
  if (typeof details === 'object' && details !== null && 'reason' in details) {
    const reason = (details as Record<string, unknown>).reason
    return typeof reason === 'string' ? reason : undefined
  }
  return undefined
}

/** Normalizes any error thrown by a `callable()` invocation to a `CallableError`. */
export function parseCallableError(error: unknown): CallableError {
  // Hazard: check FunctionsError first; it also satisfies `isCallableError` and would be returned unparsed, leaving `reason` undefined.
  if (isFunctionsError(error)) {
    return {
      code: error.code,
      reason: extractReason(error.details),
      message: error.message,
    }
  }

  if (isCallableError(error)) {
    return error
  }

  if (error instanceof Error) {
    return { code: 'unknown', message: error.message }
  }

  return { code: 'unknown', message: i18n.global.t('errors.unknownError') }
}

/** Builds a type guard narrowing a `CallableError`'s `reason` to a known union of strings. */
export function createReasonGuard<T extends string>(reasons: readonly T[]) {
  return (reason: string | undefined): reason is T =>
    reason !== undefined && (reasons as readonly string[]).includes(reason)
}
